import { useAppSelector } from '../app/hooks';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchVisibilityConfig } from '../store/user/userV2/actions';
import { VIEW_CONFIGS } from './constants/commonConstants';
import { useSearchParams } from 'react-router-dom';
import type { VisibilityLevel } from './constants/visibilityKeys';
import { VISIBILITY_LEVEL } from './constants/visibilityKeys';
import * as visibilityConfigUtils from './visibilityConfigUtils';
import type { FeatureVisibility, VisibilityConfigMap } from './visibilityConfigUtils';

export type { FeatureVisibility } from './visibilityConfigUtils';

export const useVisibilityConfig = () => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const visibilityConfig = useAppSelector((state) => {
        const raw = state.user.visibilityConfig;
        return (raw?.additionalDetailsConfig ?? raw) as VisibilityConfigMap;
    });
    const visibilityConfigLoading = useAppSelector((state) => state.user.visibilityConfigLoading);
    const visibilityConfigLoaded = useAppSelector((state) => state.user.visibilityConfigLoaded);
    const userData = useAppSelector((state) => state.user.userData);

    useEffect(() => {
        if (userData?.accountType && userData?.primaryEmail && userData?.investorOrgId) {
            // Skip if config was already loaded (even if empty) or a fetch is in flight.
            if (visibilityConfigLoaded || visibilityConfigLoading) return;
            const payload = {
                accountType: searchParams.get('accountType') || userData.accountType,
                primaryEmail: userData.primaryEmail,
                investorOrgId: userData.investorOrgId,
                userRole: userData?.roles?.[0] || '',
                configName: VIEW_CONFIGS.AICA_VISIBILITY,
            };

            dispatch(fetchVisibilityConfig(payload));
        }
    }, [
        dispatch,
        userData?.accountType,
        userData?.primaryEmail,
        userData?.investorOrgId,
        userData?.roles,
        visibilityConfigLoaded,
        visibilityConfigLoading,
    ]);

    const refreshConfig = () => {
        if (userData?.accountType && userData?.primaryEmail && userData?.investorOrgId) {
            const payload = {
                accountType: userData.accountType,
                primaryEmail: userData.primaryEmail,
                investorOrgId: userData.investorOrgId,
                userRole: userData?.roles?.[0] || '',
                configName: VIEW_CONFIGS.AICA_VISIBILITY,
            };

            dispatch(fetchVisibilityConfig(payload));
        }
    };

    const getLevel = (configKey: string): VisibilityLevel => visibilityConfigUtils.getLevel(visibilityConfig, configKey);

    const isVisible = (configKey: string) => getLevel(configKey) >= VISIBILITY_LEVEL.READ;
    const isHidden = (configKey: string) => getLevel(configKey) === VISIBILITY_LEVEL.HIDDEN;
    const canRead = (configKey: string) => getLevel(configKey) >= VISIBILITY_LEVEL.READ;
    const canWrite = (configKey: string) => getLevel(configKey) === VISIBILITY_LEVEL.WRITE;
    const getFeatureVisibility = useCallback(
        (configKey: string) => visibilityConfigUtils.getFeatureVisibility(visibilityConfig, configKey),
        [visibilityConfig],
    );

    return {
        config: visibilityConfig,
        getLevel,
        isVisible,
        isHidden,
        canRead,
        canWrite,
        getFeatureVisibility,
        refreshConfig,
    };
};

export function useFeatureVisibility<T extends Record<string, string>>(
    keysObj: T,
): Record<keyof T, FeatureVisibility> & { getFeatureVisibility: (key: string) => FeatureVisibility } {
    const { getFeatureVisibility } = useVisibilityConfig();

    const visibilityMap = useMemo(() => {
        return Object.entries(keysObj).reduce(
            (acc, [alias, key]) => {
                acc[alias as keyof T] = getFeatureVisibility(key);
                return acc;
            },
            {} as Record<keyof T, FeatureVisibility>,
        );
    }, [keysObj, getFeatureVisibility]);

    return { ...visibilityMap, getFeatureVisibility };
}
